import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useEffect, useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { SysProgramStudi, Unitkerja } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox"
import { useSearchParams } from "react-router-dom"
import { getListUnitkerja } from "@/services/getData"

const postData = async (newData: SysProgramStudi) => {
  const response = await api.post("/setting/sys-prodi/store", newData)
  return response.data
}

const patchData = async (newData: SysProgramStudi) => {
  const response = await api.patch("/setting/sys-prodi/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_sys_program_studi: string }) => {
  const response = await api({
    url: "/setting/sys-prodi/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function FormProdi({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: SysProgramStudi | null
  closeModal: () => void
}) {
  const [searchParams] = useSearchParams()
  const id_sys_periode_params = searchParams.get("id")

  const { data: dataUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  })
  const dataProdi = dataUnitkerja?.filter(
    (unitkerja: Unitkerja) => unitkerja?.unitkerja_group_key == "5"
  )

  const [form, setForm] = useState({
    id_sys_program_studi: "",
    id_sys_periode: selectedRow?.id_sys_periode || id_sys_periode_params || "",
    uk_id: "",
    list_uk_id: [] as string[],
    is_kelas_denpasar: false,
    daya_tampung: 0,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJalurJenis"] })
      queryClient.invalidateQueries({
        queryKey: ["periode", id_sys_periode_params],
      })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      uk_id: selectedRow?.uk_id || "",
      id_sys_program_studi: selectedRow?.id_sys_program_studi || "",
      daya_tampung: selectedRow?.daya_tampung || 0,
      is_kelas_denpasar: selectedRow?.is_kelas_denpasar || false,
    }))
  }, [selectedRow])

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Setting Program Studi"
          description={`Apakah anda yakin akan menghapus data?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data Setting Program Studi"
            : "Update Data Setting Program Studi"
        }
      />

      <div className="grid grid-cols-3 gap-3">
        {dataProdi &&
          dataProdi.length > 0 &&
          dataProdi.map((prodi: Unitkerja) => {
            const isChecked = form.list_uk_id.includes(prodi.uk_id)
            return (
              <div key={prodi.uk_id} className="flex gap-3">
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => {
                    setForm((prev) => ({
                      ...prev,
                      list_uk_id: isChecked
                        ? prev.list_uk_id.filter(
                            (uk_id) => uk_id !== prodi.uk_id
                          )
                        : [...prev.list_uk_id, prodi.uk_id],
                    }))
                  }}
                />
                <Label>{prodi.nama}</Label>
              </div>
            )
          })}
      </div>

      {/* <div>
        <Label>Program Studi</Label>
        <Select
          required
          value={form.uk_id}
          onValueChange={(value) => {
            setForm((prev) => ({ ...prev, uk_id: value }));
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Program Studi"}>
              {form.uk_id &&
                dataProdi?.find((prodi: Unitkerja) => prodi.uk_id == form.uk_id)
                  ?.nama}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataProdi?.length > 0 &&
              dataProdi?.map((prodi: Unitkerja) => (
                <SelectItem key={prodi.uk_id} value={prodi.uk_id || ""}>
                  {prodi.nama}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div> */}
      <div>
        <Label>Daya Tampung</Label>
        <Input
          required
          type="number"
          placeholder="Ketik daya tampung..."
          value={form.daya_tampung}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              daya_tampung: Number(event.target.value),
            }))
          }
        />
      </div>
      <div className="flex gap-3">
        <Checkbox
          id="is_kelas_denpasar"
          checked={form.is_kelas_denpasar}
          onCheckedChange={(value) =>
            setForm((prev) => ({ ...prev, is_kelas_denpasar: Boolean(value) }))
          }
        />
        <Label htmlFor="is_kelas_denpasar">Kelas Denpasar</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
